import { useState } from 'react';
import { useActivityTimeline, useActivitySummary } from '../../hooks/useActivity';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/Card';
import { Input } from '../../components/ui/Input';
import { Button } from '../../components/ui/Button';
import { formatDuration } from '../../lib/utils';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';

export default function ActivityPage() {
  const [dateRange, setDateRange] = useState({
    startDate: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0],
  });
  const [granularity, setGranularity] = useState<'hour' | 'day' | 'week'>('day');

  const { data: timelineData, isLoading: loadingTimeline } = useActivityTimeline({
    ...dateRange,
    granularity,
  });

  const { data: summary, isLoading: loadingSummary } = useActivitySummary(dateRange);

  const chartData = timelineData?.data?.map((item: any) => ({
    ...item,
    activeMinutes: Math.round(item.activeSeconds / 60),
    idleMinutes: Math.round(item.idleSeconds / 60),
  }));

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Activity</h1>
          <p className="text-gray-600">Track productivity metrics</p>
        </div>
        <div className="flex items-center gap-4">
          <Input
            type="date"
            value={dateRange.startDate}
            onChange={(e) => setDateRange((prev) => ({ ...prev, startDate: e.target.value }))}
            className="w-auto"
          />
          <span className="text-gray-500">to</span>
          <Input
            type="date"
            value={dateRange.endDate}
            onChange={(e) => setDateRange((prev) => ({ ...prev, endDate: e.target.value }))}
            className="w-auto"
          />
          <div className="flex border rounded-md">
            {(['hour', 'day', 'week'] as const).map((g) => (
              <Button
                key={g}
                variant={granularity === g ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setGranularity(g)}
                className="rounded-none first:rounded-l-md last:rounded-r-md"
              >
                {g.charAt(0).toUpperCase() + g.slice(1)}
              </Button>
            ))}
          </div>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Total Active Time</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold">
              {loadingSummary ? '...' : formatDuration(summary?.totalActiveSeconds || 0)}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Average Activity</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold">
              {loadingSummary ? '...' : `${summary?.averageActivityPercent || 0}%`}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Key Presses</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold">
              {loadingSummary ? '...' : (summary?.totalKeyPresses || 0).toLocaleString()}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Mouse Clicks</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold">
              {loadingSummary ? '...' : (summary?.totalMouseClicks || 0).toLocaleString()}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Soft, informational-only signal — not a stat card by design, since
          it's a best-effort heuristic (see backend/desktop-agent synthetic
          input scoring) and must not read with the same weight/certainty
          as the real metrics above. Only shown when at least one period in
          range actually had enough data to be scored. */}
      {!loadingSummary && summary?.avgSyntheticInputScore !== null && summary?.avgSyntheticInputScore !== undefined && (
        <p
          className="text-xs text-gray-400 -mt-2"
          title="A best-effort signal computed from keystroke rhythm, mouse path shape, and click timing — not proof of anything, and never a substitute for human review. See the backend/desktop-agent code for full documented limitations and false-positive risks (e.g. remote-desktop input, accessibility devices)."
        >
          Input pattern signal: {summary.avgSyntheticInputScore.toFixed(1)}/100 avg over{' '}
          {summary.scoredPeriodCount} scored period{summary.scoredPeriodCount === 1 ? '' : 's'} (higher =
          more automated-looking; informational only)
        </p>
      )}

      {/* Activity Chart */}
      <Card>
        <CardHeader>
          <CardTitle>Activity Timeline</CardTitle>
        </CardHeader>
        <CardContent>
          {loadingTimeline ? (
            <div className="h-80 flex items-center justify-center text-gray-500">Loading...</div>
          ) : chartData?.length === 0 ? (
            <div className="h-80 flex items-center justify-center text-gray-500">
              No activity data for this period
            </div>
          ) : (
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="timestamp"
                    tickFormatter={(value) => {
                      if (granularity === 'hour') {
                        return value.split(' ')[1] || value;
                      }
                      return value;
                    }}
                  />
                  <YAxis
                    label={{ value: 'Minutes', angle: -90, position: 'insideLeft' }}
                  />
                  <Tooltip
                    formatter={(value: number, name: string) => [
                      `${value} mins`,
                      name === 'activeMinutes' ? 'Active' : 'Idle',
                    ]}
                  />
                  <Area
                    type="monotone"
                    dataKey="activeMinutes"
                    stackId="1"
                    stroke="#3b82f6"
                    fill="#3b82f6"
                    name="Active"
                  />
                  <Area
                    type="monotone"
                    dataKey="idleMinutes"
                    stackId="1"
                    stroke="#e5e7eb"
                    fill="#e5e7eb"
                    name="Idle"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Activity Percentage Chart */}
      <Card>
        <CardHeader>
          <CardTitle>Activity Rate</CardTitle>
        </CardHeader>
        <CardContent>
          {loadingTimeline ? (
            <div className="h-60 flex items-center justify-center text-gray-500">Loading...</div>
          ) : chartData?.length === 0 ? (
            <div className="h-60 flex items-center justify-center text-gray-500">
              No activity data for this period
            </div>
          ) : (
            <div className="h-60">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="timestamp"
                    tickFormatter={(value) => {
                      if (granularity === 'hour') {
                        return value.split(' ')[1] || value;
                      }
                      return value;
                    }}
                  />
                  <YAxis domain={[0, 100]} tickFormatter={(value) => `${value}%`} />
                  <Tooltip formatter={(value: number) => [`${value}%`, 'Activity Rate']} />
                  <Area
                    type="monotone"
                    dataKey="activityPercent"
                    stroke="#10b981"
                    fill="#10b981"
                    fillOpacity={0.3}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
